/**
 * TaskFormModal — add / edit one task (Admin > Tasks), in the corporate
 * dashboard's Task modal format:
 *   - Task Name *;
 *   - Task Type (corp's coloured badges in the picker), and the settings box
 *     for it: E-Mail (Send To, Header /
 *     Footer, Email Template, documents to attach), SMS (Send To, SMS
 *     Template), Add Patient Tag (Tag), Change Plan Stage (Stage, optional
 *     Status), the survey links (Survey / E-Form + Email or SMS Template);
 *     Human and the opt-outs have none;
 *   - Notes; and, on an existing task, the task groups that use it (each opens
 *     that group when the page passes onEditGroup).
 * Corp's rules for what each type needs (taskActionChecks) keep Save off until
 * they are met — the senders silently skip a task that misses one. Changing
 * the type clears the type settings, as corp does. Widths are corp's: plain
 * pickers fit their content, searchable ones fill the row.
 * On a Locked page AdminFormModal makes it a viewer; a task group that uses
 * the task opens from a View icon instead of an Edit one.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Edit, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  TASK_TYPE_BADGE,
  TASK_TYPE_LABELS,
  TASK_TYPE_ORDER,
  UNKNOWN_TYPE_BADGE,
  createAdminTask,
  getStageStatus,
  listDocumentOptions,
  listDocumentPacketOptions,
  listEmailTemplateOptions,
  listHeaderFooterOptions,
  listSmsTemplateOptions,
  listStageOptions,
  listStageStatuses,
  listSurveyOptions,
  listTagOptions,
  taskActionChecks,
  updateAdminTask,
  type AdminTask,
  type DocumentOption,
  type StageOption,
  type StatusOption,
  type TaskOption,
} from '@/services/tasksAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DESCRIPTION_CLASS, DialogField, LABEL_CLASS, SelectedRows, fieldClasses } from './DialogChrome';
import { useTrackedForm } from './useTrackedForm';

/** A type badge in corp's colours. */
export function TaskTypeBadge({ autoTaskID, className }: { autoTaskID: number; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        TASK_TYPE_BADGE[autoTaskID] ?? UNKNOWN_TYPE_BADGE,
        className,
      )}
    >
      {TASK_TYPE_LABELS[autoTaskID] ?? 'Unknown'}
    </span>
  );
}

interface TaskForm {
  name: string;
  /** 8 also stands for 82 (a stage plus a status). */
  autoTaskID: number;
  sendToID: number;
  headerFooterID: number;
  contentID: number;
  /** Change Plan Stage: the stage (what an 8 stores) and the optional status (an 82). */
  stageID: number;
  statusID: number;
  attachments: string[];
  notes: string;
}

function formFrom(task: AdminTask | null): TaskForm {
  const type = task?.autoTaskID ?? 0;
  return {
    name: task?.name ?? '',
    autoTaskID: type === 82 ? 8 : type,
    sendToID: task?.sendToID ?? 0,
    headerFooterID: task?.headerFooterID ?? 0,
    contentID: type === 8 || type === 82 ? 0 : (task?.contentID ?? 0),
    stageID: type === 8 ? (task?.contentID ?? 0) : 0,
    statusID: type === 82 ? (task?.contentID ?? 0) : 0,
    attachments: (task?.attachments ?? '').split(',').filter((a) => a !== '' && a !== '0'),
    notes: task?.notes ?? '',
  };
}

/** The four action columns as saved: an 8 with a status is an 82 storing the status. */
export function taskPayload(f: TaskForm, originalAttachments: string) {
  const type = f.autoTaskID;
  const toStatus = type === 8 && f.statusID > 0;
  return {
    name: f.name.trim(),
    autoTaskID: toStatus ? 82 : type,
    sendToID: f.sendToID,
    headerFooterID: f.headerFooterID,
    contentID: type === 8 ? (toStatus ? f.statusID : f.stageID) : f.contentID,
    // Only E-Mail attaches documents; another type keeps what it had.
    attachments: type === 1 ? f.attachments.join(',') : originalAttachments,
    notes: f.notes,
  };
}

/** The type's option lists, loaded when the type needs them. */
interface Options {
  headerFooters?: TaskOption[];
  emailTemplates?: TaskOption[];
  smsTemplates?: TaskOption[];
  documents?: DocumentOption[];
  packets?: DocumentOption[];
  surveys?: TaskOption[];
  tags?: TaskOption[];
  stages?: StageOption[];
}

const NEEDS: Record<number, (keyof Options)[]> = {
  1: ['headerFooters', 'emailTemplates', 'documents', 'packets'],
  2: ['smsTemplates'],
  6: ['tags'],
  7: ['surveys', 'emailTemplates'],
  8: ['stages'],
  9: ['surveys', 'smsTemplates'],
};

const LOADERS: Record<keyof Options, () => Promise<unknown[]>> = {
  headerFooters: listHeaderFooterOptions,
  emailTemplates: listEmailTemplateOptions,
  smsTemplates: listSmsTemplateOptions,
  documents: listDocumentOptions,
  packets: listDocumentPacketOptions,
  surveys: listSurveyOptions,
  tags: listTagOptions,
  stages: listStageOptions,
};

/** Active choices, plus the saved one even if it was since retired. */
function choices(list: TaskOption[] | undefined, current: number, noun: string) {
  const out = (list ?? []).filter((o) => o.active || o.id === current).map((o) => ({ value: o.id, label: o.label }));
  if (current > 0 && list && !out.some((o) => o.value === current)) out.push({ value: current, label: `${noun} #${current} (unavailable)` });
  return out;
}

function SettingsBox({ title, error, children }: { title: string; error: boolean; children: ReactNode }) {
  return (
    <div className={cn('space-y-3 rounded-lg border bg-slate-50 p-3', error ? fieldClasses.dashed('error') : 'border-slate-200')}>
      <p className="text-xs font-medium uppercase text-slate-500">{title}</p>
      {children}
    </div>
  );
}

export interface TaskGroupUse {
  taskGroupID: number;
  name: string;
  active: boolean;
}

export function TaskFormModal({
  task,
  groups = [],
  onEditGroup,
  onClose,
  onSaved,
}: {
  /** null = a new task. */
  task: AdminTask | null;
  /** The task groups that use it. */
  groups?: TaskGroupUse[];
  onEditGroup?: (taskGroupID: number) => void;
  onClose: () => void;
  onSaved: (taskID: number) => void;
}) {
  const isNew = task === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<TaskForm>(() => formFrom(task), { tracking: !isNew });
  const f = t.form;
  const [options, setOptions] = useState<Options>({});
  const [statuses, setStatuses] = useState<StatusOption[] | null>(null);
  const [resolving, setResolving] = useState(task?.autoTaskID === 82);
  const [docFilter, setDocFilter] = useState('ALL');
  const [saving, setSaving] = useState(false);
  const { titles } = useRoleTitles();

  // Load what the chosen type picks from (once per list).
  useEffect(() => {
    const missing = (NEEDS[f.autoTaskID] ?? []).filter((k) => options[k] === undefined);
    if (missing.length === 0) return;
    let live = true;
    void Promise.all(missing.map((k) => LOADERS[k]().catch(() => [])))
      .then((lists) => {
        if (!live) return;
        setOptions((prev) => ({ ...prev, ...Object.fromEntries(missing.map((k, i) => [k, lists[i]])) }));
      });
    return () => {
      live = false;
    };
  }, [f.autoTaskID, options]);

  // A saved status (82): find the stage it belongs to, so the form shows both.
  useEffect(() => {
    if (!resolving || !options.stages || !task) return;
    let live = true;
    void getStageStatus(task.contentID).then((status) => {
      if (!live) return;
      const stage = status ? options.stages!.find((s) => s.accountStageID === status.accountStageID || s.id === status.accountStageID) : undefined;
      if (stage) t.reset({ ...t.saved, stageID: stage.id });
      setResolving(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolving, options.stages, task]);

  // The chosen stage's statuses.
  const stage = options.stages?.find((s) => s.id === f.stageID);
  useEffect(() => {
    setStatuses(null);
    if (!stage) return;
    let live = true;
    void listStageStatuses(stage.accountStageID)
      .then((list) => live && setStatuses(list))
      .catch(() => live && setStatuses([]));
    return () => {
      live = false;
    };
  }, [stage]);

  const type = f.autoTaskID;
  const checks = taskActionChecks({
    autoTaskID: type,
    sendToID: f.sendToID,
    contentID: type === 8 ? f.stageID : f.contentID,
    headerFooterID: f.headerFooterID,
    name: f.name,
  });
  const err = (k: Parameters<typeof checks.errors.has>[0]) => checks.errors.has(k);
  const loadingType = (NEEDS[type] ?? []).some((k) => options[k] === undefined) || resolving;

  const role = (key: string, fallback: string) => titles[key] ?? fallback;
  const sendToOptions =
    type === 1
      ? [
          { value: 0, label: 'Not Specified' },
          { value: 1, label: 'Patient' },
          { value: 2, label: role('Coordinator', 'Coordinator') },
          { value: 3, label: role('Doctor', 'Doctor') },
          { value: 4, label: role('Nurse', 'Nurse') },
        ]
      : [
          { value: 0, label: 'Not Specified' },
          { value: 1, label: 'Patient' },
        ];

  const allDocs = useMemo(() => [...(options.documents ?? []), ...(options.packets ?? [])], [options.documents, options.packets]);
  const docCategories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const d of options.documents ?? []) if (d.categoryID && d.categoryName && !seen.has(d.categoryID)) seen.set(d.categoryID, d.categoryName);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [options.documents]);
  const docChoices = useMemo(() => {
    const docs = docFilter === 'PACKETS' ? [] : (options.documents ?? []).filter((d) => docFilter === 'ALL' || d.categoryID === docFilter);
    const packets = docFilter === 'ALL' || docFilter === 'PACKETS' ? (options.packets ?? []) : [];
    return [...docs, ...packets].map((d) => ({ value: d.value, label: d.label }));
  }, [docFilter, options.documents, options.packets]);

  function changeType(next: number) {
    // Corp: a new type starts with empty settings.
    t.patch({ autoTaskID: next, sendToID: 0, headerFooterID: 0, contentID: 0, stageID: 0, statusID: 0 });
  }

  async function save() {
    if (checks.messages.length > 0) return;
    setSaving(true);
    const data = taskPayload(f, task?.attachments ?? '');
    try {
      let taskID: number;
      if (task) {
        await updateAdminTask(task.taskID, data);
        taskID = task.taskID;
      } else {
        taskID = await createAdminTask(data);
      }
      toast.success(task ? `"${data.name}" updated successfully` : 'Task created');
      onSaved(taskID);
    } catch (e) {
      toast.error(getErrorMessage(e, 'The task could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'New Task' : 'Edit Task'}
      isNew={isNew}
      dirty={isNew ? t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={checks.messages}
      saveLabel={isNew ? 'Create' : 'Save'}
      canSave={!loadingType}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <DialogField
          label="Task Name"
          htmlFor="task-name"
          required
          state={err('name') ? 'error' : 'none'}
          labelHint={err('name') ? '— required' : undefined}
          badge={t.badge('name', 'Task Name')}
        >
          <Input
            id="task-name"
            value={f.name}
            maxLength={100}
            placeholder="Task name"
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(err('name') ? 'error' : 'none')}
          />
        </DialogField>

        <DialogField label="Task Type" htmlFor="task-type" badge={t.badge('autoTaskID', 'Task Type')}>
          {/* Corp's picker: the type badges are the options, and the closed field shows the chosen one. */}
          <Select<number>
            id="task-type"
            options={TASK_TYPE_ORDER.map((id) => ({ value: id, label: TASK_TYPE_LABELS[id] }))}
            value={type}
            onChange={(v) => v != null && v !== type && changeType(v)}
            renderOption={(o) => <TaskTypeBadge autoTaskID={o.value} />}
            renderValue={(o) => <TaskTypeBadge autoTaskID={o.value} />}
            wrapperClassName="w-fit"
          />
        </DialogField>

        {loadingType && NEEDS[type] ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            {type === 1 && (
              <SettingsBox title="E-Mail Settings" error={err('sendTo') || err('template')}>
                <SendTo value={f.sendToID} options={sendToOptions} error={err('sendTo')} badge={t.badge('sendToID', 'Send To')} onChange={(v) => t.patch({ sendToID: v })} />
                <DialogField label="Header / Footer" htmlFor="task-header-footer" badge={t.badge('headerFooterID', 'Header / Footer')}>
                  <SearchableCombobox<number>
                    id="task-header-footer"
                    options={choices(options.headerFooters, f.headerFooterID, 'Header/footer')}
                    value={f.headerFooterID || null}
                    onChange={(v) => t.patch({ headerFooterID: v ?? 0 })}
                    placeholder="Select header/footer…"
                    clearable
                  />
                </DialogField>
                <TemplateField id="task-email-template" label="Email Template" list={options.emailTemplates} value={f.contentID} error={err('template')} badge={t.badge('contentID', 'Email Template')} onChange={(v) => t.patch({ contentID: v })} />
                <DialogField label="Document Category" htmlFor="task-doc-category">
                  <Select<string>
                    id="task-doc-category"
                    options={[{ value: 'ALL', label: 'All Documents' }, { value: 'PACKETS', label: 'Document Packets' }, ...docCategories]}
                    value={docFilter}
                    onChange={(v) => setDocFilter(v ?? 'ALL')}
                    searchable
                  />
                </DialogField>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <label htmlFor="task-attachments" className={LABEL_CLASS}>
                      Attach Documents
                    </label>
                    {t.badge('attachments', 'Attach Documents')}
                  </div>
                  <SearchableMultiSelect
                    id="task-attachments"
                    options={docChoices}
                    selectedValues={f.attachments}
                    onSelectionChange={(next) => {
                      // Keep what the category filter hides; the picker only reports what it shows.
                      const shown = new Set(docChoices.map((d) => d.value));
                      t.patch({ attachments: [...f.attachments.filter((a) => !shown.has(a)), ...next.filter((a) => shown.has(a))] });
                    }}
                    placeholder="Search and select documents…"
                    searchPlaceholder="Search documents…"
                    emptyMessage="No documents in this category."
                  />
                  <SelectedRows<string>
                    rows={f.attachments.map((a) => {
                      const doc = allDocs.find((d) => d.value === a);
                      return doc ? { id: a, label: doc.label } : { id: a, label: `Document ${a}`, unavailable: true };
                    })}
                    removeLabel="Remove"
                    onRemove={(id) => t.patch({ attachments: f.attachments.filter((a) => a !== id) })}
                  />
                </div>
              </SettingsBox>
            )}

            {type === 2 && (
              <SettingsBox title="SMS Settings" error={err('sendTo') || err('template')}>
                <SendTo value={f.sendToID} options={sendToOptions} error={err('sendTo')} badge={t.badge('sendToID', 'Send To')} onChange={(v) => t.patch({ sendToID: v })} />
                <TemplateField id="task-sms-template" label="SMS Template" list={options.smsTemplates} value={f.contentID} error={err('template')} badge={t.badge('contentID', 'SMS Template')} onChange={(v) => t.patch({ contentID: v })} />
              </SettingsBox>
            )}

            {type === 6 && (
              <SettingsBox title="Patient Tag" error={err('tag')}>
                <TemplateField id="task-tag" label="Tag" noun="Tag" list={options.tags} value={f.contentID} error={err('tag')} badge={t.badge('contentID', 'Tag')} onChange={(v) => t.patch({ contentID: v })} />
              </SettingsBox>
            )}

            {type === 8 && (
              <SettingsBox title="Change Plan Stage" error={err('stage')}>
                <div className="grid gap-3 md:grid-cols-2">
                  <DialogField
                    label="Stage"
                    htmlFor="task-stage"
                    required
                    state={err('stage') ? 'error' : 'none'}
                    labelHint={err('stage') ? '— required' : undefined}
                    badge={t.badge('stageID', 'Stage')}
                  >
                    <SearchableCombobox<number>
                      id="task-stage"
                      options={(options.stages ?? []).map((s) => ({ value: s.id, label: s.label }))}
                      value={f.stageID || null}
                      onChange={(v) => t.patch({ stageID: v ?? 0, statusID: 0 })}
                      placeholder="Select stage…"
                    />
                  </DialogField>
                  <DialogField label="Status" htmlFor="task-status" badge={t.badge('statusID', 'Status')} description="Optional: also set the plan's status.">
                    <SearchableCombobox<number>
                      id="task-status"
                      options={(statuses ?? []).map((s) => ({ value: s.id, label: s.label }))}
                      value={f.statusID || null}
                      onChange={(v) => t.patch({ statusID: v ?? 0 })}
                      placeholder={!f.stageID ? 'Select a stage first…' : statuses === null ? 'Loading…' : 'Select status…'}
                      disabled={!f.stageID || statuses === null}
                      emptyMessage="No statuses for this stage."
                      clearable
                    />
                  </DialogField>
                </div>
              </SettingsBox>
            )}

            {(type === 7 || type === 9) && (
              <SettingsBox title={type === 7 ? 'E-mail Survey Link Settings' : 'SMS Survey Link Settings'} error={err('survey') || err('template')}>
                <TemplateField id="task-survey" label="Survey / E-Form" noun="Survey" list={options.surveys} value={f.headerFooterID} error={err('survey')} badge={t.badge('headerFooterID', 'Survey / E-Form')} onChange={(v) => t.patch({ headerFooterID: v })} />
                <TemplateField
                  id="task-survey-template"
                  label={type === 7 ? 'Email Template' : 'SMS Template'}
                  list={type === 7 ? options.emailTemplates : options.smsTemplates}
                  value={f.contentID}
                  error={err('template')}
                  badge={t.badge('contentID', type === 7 ? 'Email Template' : 'SMS Template')}
                  onChange={(v) => t.patch({ contentID: v })}
                />
              </SettingsBox>
            )}
          </>
        )}

        <DialogField label="Notes" htmlFor="task-notes" badge={t.badge('notes', 'Notes')}>
          <Textarea id="task-notes" rows={3} value={f.notes} onChange={(e) => t.patch({ notes: e.target.value })} />
        </DialogField>

        {!isNew && (
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <p className="text-xs font-medium uppercase text-slate-500">Task Groups Using This Task</p>
            {groups.length === 0 ? (
              <p className={DESCRIPTION_CLASS}>No task groups use this task.</p>
            ) : (
              <div className="space-y-1">
                {groups.map((g) => (
                  <div key={g.taskGroupID} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                    <span className={cn('truncate', !g.active && 'text-slate-400 line-through')}>{g.name}</span>
                    {onEditGroup && (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        aria-label={`${locked ? 'View' : 'Edit'} task group ${g.name}`}
                        title={locked ? 'View task group' : 'Edit task group'}
                        leftIcon={locked ? <Eye className="h-3.5 w-3.5" /> : <Edit className="h-3.5 w-3.5" />}
                        onClick={() => onEditGroup(g.taskGroupID)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminFormModal>
  );
}

function SendTo({
  value,
  options,
  error,
  badge,
  onChange,
}: {
  value: number;
  options: { value: number; label: string }[];
  error: boolean;
  badge: ReactNode;
  onChange: (v: number) => void;
}) {
  return (
    <DialogField label="Send To" htmlFor="task-send-to" required state={error ? 'error' : 'none'} labelHint={error ? '— required' : undefined} badge={badge}>
      <Select<number> id="task-send-to" options={options} value={value} onChange={(v) => onChange(v ?? 0)} wrapperClassName="w-fit" />
    </DialogField>
  );
}

function TemplateField({
  id,
  label,
  noun = 'Template',
  list,
  value,
  error,
  badge,
  onChange,
}: {
  id: string;
  label: string;
  noun?: string;
  list: TaskOption[] | undefined;
  value: number;
  error: boolean;
  badge: ReactNode;
  onChange: (v: number) => void;
}) {
  return (
    <DialogField label={label} htmlFor={id} required state={error ? 'error' : 'none'} labelHint={error ? '— required' : undefined} badge={badge}>
      <SearchableCombobox<number>
        id={id}
        options={choices(list, value, noun)}
        value={value || null}
        onChange={(v) => onChange(v ?? 0)}
        placeholder={`Select ${label.toLowerCase()}…`}
        emptyMessage={`No ${label.toLowerCase()} found.`}
      />
    </DialogField>
  );
}
